import { useState, useEffect, useCallback } from 'react'
import type { Language } from '@/types'
import { localStorageGet, localStorageSet } from '@/lib/storage'
import { getTextDirection, isRTL } from '@/lib/rtl'
import { useLanguageContext } from '@/hooks/LanguageContext'

const LANGUAGE_STORAGE_KEY = 'qr-menu-language'

// ============================================================================
// Hook: useLanguage
// Uses shared context so all components stay in sync.
// Falls back to standalone state if no provider is present (e.g. isolated use).
// ============================================================================

export interface UseLanguageReturn {
  language: Language
  setLanguage: (lang: Language) => void
  isRTL: boolean
  textDirection: 'rtl' | 'ltr'
  availableLanguages: Language[]
}

export function useLanguage(defaultLang: Language = 'fr'): UseLanguageReturn {
  // Try to use the shared context first
  try {
    const context = useLanguageContext()
    return context
  } catch {
    // No provider — fall back to standalone local state
  }

  const [language, setLanguageState] = useState<Language>(() => {
    return localStorageGet(LANGUAGE_STORAGE_KEY, defaultLang)
  })

  const [availableLanguages] = useState<Language[]>(['fr', 'ar', 'en'])

  useEffect(() => {
    const dir = getTextDirection(language)
    document.documentElement.setAttribute('dir', dir)
    document.documentElement.setAttribute('lang', language)
  }, [language])

  const setLanguage = useCallback((lang: Language) => {
    setLanguageState(lang)
    localStorageSet(LANGUAGE_STORAGE_KEY, lang)
  }, [])

  return {
    language,
    setLanguage,
    isRTL: isRTL(language),
    textDirection: getTextDirection(language),
    availableLanguages,
  }
}

// ============================================================================
// Hook: useTranslation (for component-level translations)
// ============================================================================

interface Translations {
  [key: string]: {
    [lang in Language]: string
  }
}

export function useTranslation(translations: Translations) {
  const { language } = useLanguage()

  return useCallback((key: string): string => {
    const translation = translations[key]
    if (!translation) {
      return key
    }
    return translation[language] || translation.fr || key
  }, [language])
}

// ============================================================================
// Common translations
// ============================================================================

export const commonTranslations: Translations = {
  // Navigation
  'menu': { fr: 'Menu', ar: 'القائمة', en: 'Menu' },
  'search': { fr: 'Rechercher', ar: 'بحث', en: 'Search' },
  'cart': { fr: 'Panier', ar: 'سلة', en: 'Cart' },
  'favorites': { fr: 'Favoris', ar: 'المفضلة', en: 'Favorites' },

  // Cart
  'add_to_cart': { fr: 'Ajouter', ar: 'إضافة', en: 'Add' },
  'remove': { fr: 'Supprimer', ar: 'إزالة', en: 'Remove' },
  'quantity': { fr: 'Quantité', ar: 'الكمية', en: 'Quantity' },
  'subtotal': { fr: 'Sous-total', ar: 'المجموع الفرعي', en: 'Subtotal' },
  'total': { fr: 'Total', ar: 'المجموع', en: 'Total' },
  'empty_cart': { fr: 'Votre panier est vide', ar: 'سلة التسوق فارغة', en: 'Your cart is empty' },
  'order_on_whatsapp': { fr: 'Commander sur WhatsApp', ar: 'اطلب عبر واتساب', en: 'Order on WhatsApp' },
  'special_instructions': { fr: 'Instructions spéciales (allergies, préférences...)', ar: 'تعليمات خاصة (حساسية، تفضيلات...)', en: 'Special instructions (allergies, preferences...)' },

  // Order
  'new_order': { fr: 'Nouvelle commande', ar: 'طلب جديد', en: 'New Order' },
  'order_sent': { fr: 'Commande envoyée sur WhatsApp ✓', ar: 'تم إرسال الطلب عبر واتساب ✓', en: 'Order sent on WhatsApp ✓' },
  'table': { fr: 'Table', ar: 'طاولة', en: 'Table' },

  // Search
  'search_placeholder': { fr: 'Rechercher un plat...', ar: 'البحث عن طبق...', en: 'Search for a dish...' },
  'no_results': { fr: 'Aucun résultat pour', ar: 'لا توجد نتائج لـ', en: 'No results for' },
  'results_count': { fr: 'résultats', ar: 'نتائج', en: 'results' },

  // Items
  'popular': { fr: 'Populaire', ar: 'شائع', en: 'Popular' },
  'from_price': { fr: 'À partir de', ar: 'يبدأ من', en: 'From' },
  'unavailable': { fr: 'Non disponible', ar: 'غير متوفر', en: 'Unavailable' },

  // Error states
  'loading_error': { fr: 'Erreur de chargement', ar: 'خطأ في التحميل', en: 'Loading error' },
  'try_again': { fr: 'Réessayer', ar: 'حاول مرة أخرى', en: 'Try again' },

  // Footer
  'hours': { fr: 'Horaires', ar: 'ساعات العمل', en: 'Hours' },
  'address': { fr: 'Adresse', ar: 'العنوان', en: 'Address' },
  'call': { fr: 'Appeler', ar: 'اتصال', en: 'Call' },
  'powered_by': { fr: 'Propulsé par', ar: 'مدعوم بواسطة', en: 'Powered by' },
}

export function useCommonTranslation() {
  const { language } = useLanguage()

  return useCallback((key: keyof typeof commonTranslations): string => {
    const entry = commonTranslations[key]
    const result = entry?.[language] || entry?.fr || key
    return String(result)
  }, [language])
}
